import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

/**
 * Out-of-Spec / Nonconformity modal.
 *
 * Legacy parity: frmOutOfSpec.vb — severity classifier (Minor/Major/Critical)
 * and a course of action (Scrap / Leave as is / File DMR).
 *
 * Props unchanged from the previous custom-styled version. Parent gates
 * with `{showOutOfSpecModal && (<OutOfSpecModal ... />)}`, so we render
 * with isOpen={true} unconditionally — closing happens via onClose
 * which unmounts us in the parent.
 */
const OutOfSpecModal = ({
  onClose,
  onConfirm,
  busy = false,
  // Round DI-2 (Issue 13): whether "File DMR" can create the Epicor
  // Nonconformance right now (config-gated server-side). When false,
  // File DMR warns and asks a controlled confirmation before proceeding
  // with the local-save-and-email-only path.
  epicorDmrEnabled = false,
}) => {
  const [classification, setClassification] = useState("Minor");
  const [action, setAction] = useState("scrap");
  // Confirm step when File DMR cannot reach Epicor.
  const [confirmLocalDmr, setConfirmLocalDmr] = useState(false);
  // UAT Tests 14/15/16 ROOT-CAUSE FIX: validationMsg / setValidationMsg
  // were referenced below but the state was never declared, so opening
  // this modal threw a ReferenceError that unmounted the whole React
  // tree — the "window becomes blank after Save Changes in the
  // out-of-spec / nonconformity flow" defect. Declaring the state
  // restores the flow; no behavior beyond the fix is changed.
  const [validationMsg, setValidationMsg] = useState("");

  useEffect(() => {
    setClassification("Minor");
    setAction("scrap");
  }, []);

  const payload = useMemo(
    () => ({
      classification,
      scrap: action === "scrap",
      leave: action === "leave",
      dmr: action === "dmr",
    }),
    [classification, action]
  );

  const handleClassificationChange = useCallback(
    (e) => setClassification(e.target.value),
    []
  );
  const handleActionChange = useCallback((e) => {
    setAction(e.target.value);
    setConfirmLocalDmr(false); // changing the action resets the DMR confirm step
  }, []);
  const handleConfirm = useCallback(() => {
    // Legacy frmOutOfSpec validation order and messages (verbatim):
    if (!classification) {
      setValidationMsg("Please select noncomformance severity level.");
      return;
    }
    if (!action) {
      setValidationMsg("Please select a course of action.");
      return;
    }
    setValidationMsg("");
    // Round DI-2 (Issue 13) — File DMR with Epicor disabled/unavailable:
    // controlled confirmation before finalizing the local-only path.
    if (action === "dmr" && !epicorDmrEnabled && !confirmLocalDmr) {
      setConfirmLocalDmr(true);
      return;
    }
    onConfirm?.(payload);
  }, [onConfirm, payload, classification, action, epicorDmrEnabled, confirmLocalDmr]);

  return (
    <Modal isOpen toggle={onClose} centered>
      <ModalHeader toggle={onClose}>
        <i className="ri-alert-line align-bottom me-2 text-danger"></i>
        Nonconformity Detected
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Please classify the issue and select an action to proceed.
        </p>
        {validationMsg ? (
          <div className="alert alert-warning py-2 px-3 small" role="alert">
            {validationMsg}
          </div>
        ) : null}
        <Form onSubmit={(e) => e.preventDefault()}>
          <FormGroup>
            <Label for="oos-severity">Severity</Label>
            <Input
              id="oos-severity"
              type="select"
              value={classification}
              onChange={handleClassificationChange}
            >
              <option value="">Select severity...</option>
              <option value="Minor">Minor</option>
              <option value="Major">Major</option>
              <option value="Critical">Critical</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="d-block mb-2">Action</Label>
            <FormGroup check className="mb-1">
              <Input
                type="radio"
                id="oos-action-scrap"
                name="oos-action"
                value="scrap"
                checked={action === "scrap"}
                onChange={handleActionChange}
              />
              <Label check for="oos-action-scrap">
                Scrap
              </Label>
            </FormGroup>
            <FormGroup check className="mb-1">
              <Input
                type="radio"
                id="oos-action-leave"
                name="oos-action"
                value="leave"
                checked={action === "leave"}
                onChange={handleActionChange}
              />
              <Label check for="oos-action-leave">
                Leave as is
              </Label>
            </FormGroup>
            <FormGroup check>
              <Input
                type="radio"
                id="oos-action-dmr"
                name="oos-action"
                value="dmr"
                checked={action === "dmr"}
                onChange={handleActionChange}
              />
              <Label check for="oos-action-dmr">
                File DMR
              </Label>
            </FormGroup>
          </FormGroup>

          {/* Round DI-2 (Issue 13) — File DMR while Epicor creation is
              disabled/unavailable: warn up front, then require an explicit
              confirmation of the local-save-and-email-only path. */}
          {action === "dmr" && !epicorDmrEnabled ? (
            <div
              className={
                confirmLocalDmr
                  ? "alert alert-danger py-2 px-3 small mt-3 mb-0"
                  : "alert alert-warning py-2 px-3 small mt-3 mb-0"
              }
              role="alert"
            >
              <i className="ri-alert-line align-bottom me-1"></i>
              Epicor DMR creation is currently disabled/unavailable. This
              will be saved locally and emailed for manual DMR filing.
              {confirmLocalDmr ? (
                <div className="mt-1 fw-semibold">
                  Press &ldquo;Confirm — save locally&rdquo; to proceed
                  anyway, or choose a different action.
                </div>
              ) : null}
            </div>
          ) : null}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {/* UAT 14-16: the parent keeps this modal OPEN until the save
            actually succeeds (or shows the server error without losing
            the inspection context); `busy` disables double-submits. */}
        <Button
          color={confirmLocalDmr ? "danger" : "primary"}
          onClick={handleConfirm}
          disabled={busy}
        >
          <i className="ri-check-line align-bottom me-1"></i>
          {busy
            ? "Saving..."
            : confirmLocalDmr
            ? "Confirm — save locally"
            : "Confirm"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(OutOfSpecModal);
